import React, { useState } from "react";
import {
  EmailOutlined,
  KeyboardArrowRight,
  PersonOutline,
  Search,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import LabeledField from "../../components/form/LabeledField";
import { useToaster } from "../../hooks/useToaster";
import { SEVERITIES } from "../../data/constants";
import { formatIdentityLookupValue } from "../../utils/identityLookupFormat";
import WorkflowDateField from "./WorkflowDateField";
import {
  getInitials,
  PIF_LOOKUP_ERROR_MESSAGES,
  PifIdentityLookupError,
  PifIdentityLookupOutcome,
  PifIdentityMatch,
} from "./workflowRunData";
import { PifStartAnswers } from "./pifCaseValues";
import { PIF_LOOKUP_BY, PifLookupBy } from "./pifWorkflowKeys";

type PreApprovalGate = "yes" | "not" | "";

type LookupError = PifIdentityLookupError;

interface WorkflowPifStartStageProps {
  /** Hands over the resolved identity and pif-1's answers. The typed SSN rides along only for a new
   *  hire, for the applicant record (`newHireSsn`), and never becomes a case answer. */
  onContinue: (answers: PifStartAnswers) => void;
  /** Resolves the typed SSN / Employee ID against real records. */
  lookupIdentity: (value: string) => Promise<PifIdentityLookupOutcome>;
}

const MISMATCH_LIMIT = 2;

/** Each failed lookup explains itself in place; Continue stays off in all three. */
const LOOKUP_ERROR_MESSAGES = PIF_LOOKUP_ERROR_MESSAGES;

/** Fixed nine-dot stand-in, as in the prototype: an active employee's SSN is never returned to the
 *  client (PostIF redacts it outright), so there are no digits here to mask a real length from. */
const MASKED_SSN = "•".repeat(9);
const SSN_DIGITS = 9;
const EMPLOYEE_ID_MAX_DIGITS = 6;
const digitCount = (value: string) => value.replace(/\D/g, "").length;
const isFullSsn = (value: string) => digitCount(value) === SSN_DIGITS;
/** Too long for an Employee ID, too short for an SSN. */
const isPartialSsn = (value: string) => digitCount(value) > EMPLOYEE_ID_MAX_DIGITS && !isFullSsn(value);

const WorkflowPifStartStage: React.FC<WorkflowPifStartStageProps> = ({
  onContinue,
  lookupIdentity,
}) => {
  const showToaster = useToaster();
  const [gate, setGate] = useState<PreApprovalGate>("");
  const [lookupBy, setLookupBy] = useState<PifLookupBy>(PIF_LOOKUP_BY.ssn);
  const [identityValue, setIdentityValue] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [match, setMatch] = useState<PifIdentityMatch | null>(null);
  const [mismatchCount, setMismatchCount] = useState(0);
  const [lookingUp, setLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<LookupError | null>(null);
  // An Employee-ID lookup that found nobody: a new hire must be looked up by SSN (Pooja, 2026-10-08).
  const [needsSsnLookup, setNeedsSsnLookup] = useState(false);
  const [identityError, setIdentityError] = useState("");

  const handleGateChange = (_event: React.MouseEvent<HTMLElement>, value: PreApprovalGate) => {
    if (value === null) return;
    setGate(value);
  };

  const handleLookupByChange = (event: SelectChangeEvent) => {
    setLookupBy(event.target.value as PifLookupBy);
    setNeedsSsnLookup(false);
    setIdentityError("");
  };

  /**
   * A new hire is only ever identified by SSN: that is what saves their applicant record (P4) and
   * what the do-not-rehire check runs on. So an Employee-ID lookup that comes back "new" is not a
   * result — the lookup switches to SSN and the typed ID is cleared, never sent as an SSN.
   */
  const acceptMatch = (found: PifIdentityMatch) => {
    // Typed as an Employee ID whichever way "Look up by" is set - the box reads 6 digits or fewer
    // as one - so a short value that finds nobody is never sent on as a new hire's SSN.
    if ((lookupBy === PIF_LOOKUP_BY.employeeId || !isFullSsn(identityValue)) && found.flow === "new") {
      setMatch(null);
      setLookupBy(PIF_LOOKUP_BY.ssn);
      setIdentityValue("");
      setNeedsSsnLookup(true);
      return;
    }
    setNeedsSsnLookup(false);
    setMatch(found);
  };

  const handleIdentityValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Same formatter the PostIF start screen uses, so the SSN reads back as 111-11-1111 here too
    // and PIF_IDENTITY_DIRECTORY (keyed on the hyphenated SSN) can still resolve a match.
    setIdentityValue(formatIdentityLookupValue(event.target.value));
    setIdentityError("");
  };

  const handleDateOfBirthChange = (value: string) => {
    setDateOfBirth(value);
  };

  const handleLookup = async () => {
    if (!identityValue.trim()) {
      showToaster("Enter an SSN or Employee ID", SEVERITIES.INFO);
      return;
    }
    // The box reads up to 6 digits as an Employee ID and 7-9 as an SSN (formatIdentityLookupValue,
    // as the server does), so 7 or 8 digits is neither. It used to be looked up, match nobody, read
    // as a new hire, and only be refused (INVALID_SSN) after pif-1 had moved on.
    if (isPartialSsn(identityValue)) {
      setMatch(null);
      setIdentityError("An SSN is 9 digits.");
      return;
    }
    setLookupError(null);
    setLookingUp(true);
    setMatch(null);
    const outcome = await lookupIdentity(identityValue);
    setLookingUp(false);
    if (outcome.kind === "matched") acceptMatch(outcome.match);
    else setLookupError(outcome.kind);
  };

  const handleNotThisPerson = () => {
    const attempts = mismatchCount + 1;
    setMismatchCount(attempts);
    setMatch(null);
    if (attempts >= MISMATCH_LIMIT) {
      showToaster("Multiple mismatches — please contact HR", SEVERITIES.INFO);
    }
  };

  // A new hire's DOB is typed here and saved (decisions log 2); a rehire's comes from the lookup.
  const needsDateOfBirth = match?.flow === "new" && !dateOfBirth;
  const canContinue = match !== null && match.flow !== "blocked" && !needsDateOfBirth;

  const handleContinue = () => {
    if (!canContinue || !match) return;
    const digits = identityValue.replace(/\D/g, "");
    const bySsn = lookupBy === PIF_LOOKUP_BY.ssn;
    onContinue({
      match,
      lookupBy,
      dateOfBirth,
      ssnLast4: bySsn ? digits.slice(-4) : "",
      // Only a new hire's SSN is kept (decision P4); an existing employee or rehire is their Employee ID.
      newHireSsn: bySsn && match.flow === "new" && digits.length === SSN_DIGITS ? digits : undefined,
    });
  };

  return (
    <>
      <Typography component="h3" className="workflow-run__section-title">
        HR Director pre-approval
      </Typography>

      <LabeledField
        controlId="pif-gate"
        label="Has the HR Director pre-approved starting this PIF?"
        required
      >
        <ToggleButtonGroup
          id="pif-gate"
          className="workflow-run__segmented"
          exclusive
          value={gate}
          onChange={handleGateChange}
          aria-labelledby="pif-gate-label"
        >
          <ToggleButton value="yes">Yes, pre-approved</ToggleButton>
          <ToggleButton value="not">Not yet</ToggleButton>
        </ToggleButtonGroup>
      </LabeledField>

      {gate === "" && (
        <Typography className="workflow-run__hint">
          Select an option above to continue.
        </Typography>
      )}

      {gate === "not" && (
        <>
          <Alert severity="warning" className="workflow-run__alert">
            <strong>Talk to HR first.</strong> A PIF can only start after the HR Director signs off
            on the requisition. Reach out to your HR partner (Mrs. S. Blau) to get pre-approval,
            then come back and continue.
          </Alert>
          <Box className="workflow-run__actions">
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<EmailOutlined />}
              href="mailto:hr@hamaspik.org"
            >
              Email HR
            </Button>
          </Box>
        </>
      )}

      {gate === "yes" && (
        <>
          <Typography component="h3" className="workflow-run__section-title">
            Identity lookup
          </Typography>

          <Box className="workflow-run__grid">
            <LabeledField controlId="pif-lookup-by" label="Look up by">
              <FormControl size="small" fullWidth>
                <Select
                  id="pif-lookup-by"
                  labelId="pif-lookup-by-label"
                  value={lookupBy}
                  onChange={handleLookupByChange}
                >
                  <MenuItem value={PIF_LOOKUP_BY.ssn}>SSN</MenuItem>
                  <MenuItem value={PIF_LOOKUP_BY.employeeId}>Employee ID</MenuItem>
                </Select>
              </FormControl>
            </LabeledField>

            <LabeledField
              controlId="pif-identity-value"
              label="SSN or Employee ID"
              required
              error={identityError || undefined}
            >
              <Box className="workflow-run__lookup">
                <TextField
                  id="pif-identity-value"
                  size="small"
                  fullWidth
                  required
                  value={identityValue}
                  onChange={handleIdentityValueChange}
                  error={Boolean(identityError)}
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="e.g. 111-11-1111"
                />
                <Button
                  variant="contained"
                  startIcon={
                    lookingUp ? <CircularProgress size={16} color="inherit" aria-hidden="true" /> : <Search />
                  }
                  onClick={handleLookup}
                  disabled={lookingUp}
                >
                  {lookingUp ? "Looking up…" : "Look up"}
                </Button>
              </Box>
            </LabeledField>

            <LabeledField controlId="pif-date-of-birth" label="Date of birth" required>
              <WorkflowDateField
                id="pif-date-of-birth"
                value={dateOfBirth}
                onChange={handleDateOfBirthChange}
                required
              />
            </LabeledField>
          </Box>

          {lookupError && (
            <Alert severity="error" className="workflow-run__alert">
              {LOOKUP_ERROR_MESSAGES[lookupError]}
            </Alert>
          )}
          {needsSsnLookup && (
            <Alert severity="warning" className="workflow-run__alert">
              No employee found with that ID. A new hire must be looked up by SSN.
            </Alert>
          )}

          {match?.flow === "blocked" && (
            <Alert severity="error" className="workflow-run__alert">
              <strong>Employee {match.employeeId} cannot be hired through this channel.</strong>{" "}
              This record is marked Do-Not-Rehire. Please reach out to HR — the reason memo is
              internal and confidential.
            </Alert>
          )}

          {(match?.flow === "existing-active" || match?.flow === "rehire") && (
            <>
              <Box className="workflow-run__verify">
                <span className="workflow-run__avatar">{getInitials(match.name)}</span>
                <Box className="workflow-run__verify-copy">
                  <Typography className="workflow-run__verify-name">
                    {match.name}
                    <span className="labeled-field__badge">
                      {match.flow === "existing-active" ? "Identity locked" : "SSN locked"}
                    </span>
                  </Typography>
                  <Typography className="workflow-run__verify-meta">
                    {match.flow === "existing-active"
                      ? "Existing · Active employee"
                      : "Existing · Inactive — rehire"}
                  </Typography>
                  <Typography className="workflow-run__verify-facts">
                    <span>
                      <strong>Emp ID:</strong> {match.employeeId}
                    </span>
                    <span>
                      <strong>DOB:</strong> {match.dateOfBirth}
                    </span>
                    {match.flow === "existing-active" && (
                      <span className="workflow-run__verify-ssn">
                        <strong>SSN:</strong>
                        <span className="labeled-field__badge labeled-field__badge--mask">
                          {MASKED_SSN} masked
                        </span>
                      </span>
                    )}
                    {/* Decision 4.5: a rehire's SSN may show unmasked, locked — but only the digits
                        just typed here; the lookup never returns one and it is never stored. A lookup
                        by Employee ID has no SSN to show. */}
                    {match.flow === "rehire" && lookupBy === PIF_LOOKUP_BY.ssn && identityValue && (
                      <span className="workflow-run__verify-ssn">
                        <strong>SSN:</strong> {identityValue}
                        <span className="labeled-field__badge">Locked</span>
                      </span>
                    )}
                  </Typography>
                </Box>
                <Button size="small" variant="outlined" color="secondary" onClick={handleNotThisPerson}>
                  Not this person
                </Button>
              </Box>
              <Alert severity="info" className="workflow-run__alert">
                {match.flow === "existing-active" ? (
                  <>
                    Personal details are pre-filled and locked. You&apos;ll pick a{" "}
                    <strong>Position switch vs addition</strong> on the next screen.
                  </>
                ) : (
                  <>
                    Contact details (address, phone, email) will be <strong>editable</strong> next;
                    SSN carries over unmasked but locked.
                  </>
                )}
              </Alert>
            </>
          )}

          {match?.flow === "new" && (
            <Box className="workflow-run__verify">
              <span className="workflow-run__avatar workflow-run__avatar--new">
                <PersonOutline />
              </span>
              <Box className="workflow-run__verify-copy">
                <Typography className="workflow-run__verify-name">New applicant</Typography>
                <Typography className="workflow-run__verify-meta">
                  No match in Enterprise — you&apos;ll enter all details next.
                </Typography>
              </Box>
            </Box>
          )}

          {needsDateOfBirth && (
            <Typography className="workflow-run__hint">Enter the date of birth to continue.</Typography>
          )}

          <Box className="workflow-run__actions workflow-run__actions--end">
            <Button
              variant="contained"
              endIcon={<KeyboardArrowRight />}
              disabled={!canContinue}
              onClick={handleContinue}
            >
              Continue
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

export default WorkflowPifStartStage;
